import { Async } from '@client/new/helpers/types';
import { Folders, toFolder, toVideo, Video, Videos } from './library.domain';
import {
  AddFolderMutation,
  AddToFolderMutation,
  DeleteFolderMutation,
  RenameFolderMutation,
  useAddFolderMutation,
  useAddToFolderMutation,
  useDeleteFolderMutation,
  useGetLibraryContentQuery,
  useGetLibraryTagsQuery,
  useRenameFolderMutation,
} from 'generated/graphql';
import { FetchResult } from '@apollo/client';

export type LibraryModel = Async<{
  videos: Videos;
  folders: Folders;
  refetchLibraryContent: () => Promise<void>;
  addToFolder: ({
    id,
    folderIds,
    videoIds,
  }: {
    id: number | null;
    folderIds?: number[];
    videoIds?: string[];
  }) => Promise<FetchResult<AddToFolderMutation>>;
  addFolder: (p: {
    name: string;
    parentId?: number;
  }) => Promise<FetchResult<AddFolderMutation>>;
  renameFolder: (p: {
    name: string;
    id: number;
  }) => Promise<FetchResult<RenameFolderMutation>>;
  deleteFolder: (p: {
    id: number;
  }) => Promise<FetchResult<DeleteFolderMutation>>;
}>;

export const withExtrasTag =
  (taggedIds: string[]) =>
  (v: Video): Video => ({
    ...v,
    tags: [...(taggedIds.includes(v.id) ? ['extras-video'] : [])],
  });

export const useLibraryModel = (p: { creatorId: number }): LibraryModel => {
  const { creatorId } = p;
  const { data, ...q } = useGetLibraryContentQuery({
    variables: p,
    fetchPolicy: 'network-only',
  });

  const { data: tags, ...tagsQ } = useGetLibraryTagsQuery({
    variables: {
      creatorId: String(p.creatorId),
      tagInternalName: 'extras-video',
    },
    fetchPolicy: 'network-only',
  });

  const [addFolderMutation] = useAddFolderMutation();
  const [addToFolderMutation] = useAddToFolderMutation();
  const [renameFolderMutation] = useRenameFolderMutation();
  const [deleteFolderMutation] = useDeleteFolderMutation();

  if (q.error) return { type: 'fetch-error', value: q.error.message };
  if (tagsQ.error) return { type: 'fetch-error', value: tagsQ.error.message };
  if (!data || q.loading) return { type: 'fetching' };
  if (tagsQ.loading) return { type: 'fetching' };

  const videos = !data?.getCreatorsMedia
    ? []
    : data?.getCreatorsMedia
        .map(toVideo)
        .map(withExtrasTag(tags?.getContentIdsWithTag || []))
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );

  const folders = !data?.getCreatorsFolders
    ? []
    : data?.getCreatorsFolders
        .map(toFolder)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );

  return {
    type: 'fetched',
    value: {
      videos,
      folders,
      refetchLibraryContent: async () => {
        await Promise.all([q.refetch(), tagsQ.refetch()]);
      },
      addToFolder: ({ id, folderIds, videoIds }) =>
        addToFolderMutation({
          variables: {
            creatorId,
            id,
            folderIds,
            videoIds,
          },
        }),
      addFolder: ({ name, parentId }) =>
        addFolderMutation({
          variables: {
            creatorId,
            name,
            parentId,
          },
        }),
      renameFolder: ({ name, id }) =>
        renameFolderMutation({
          variables: {
            creatorId,
            name,
            id,
          },
        }),
      deleteFolder: ({ id }) =>
        deleteFolderMutation({
          variables: {
            id,
          },
        }),
    },
  };
};
